import { useState } from "react";
import { toast } from "sonner";
import { FieldLabel, Sel } from "./Fields";
import { ContactFields, ConsentBox, trackingPayload, useContactFields, validateContact } from "./LeadForm";
import { api, errMsg, useApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtHijri } from "../lib/format";

const SLOTS = Array.from({ length: 23 }, (_, i) => `${String(9 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const today = () => new Date().toISOString().slice(0, 10);

export default function ViewingForm({ propertyId, projectId, prefix = "viewing", choosable = false, onDone }) {
  const { t, tr, lang } = useI18n();
  const [v, setV, set] = useContactFields();
  const [prop, setProp] = useState(propertyId || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("in_person");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const list = useApi(choosable ? "/properties?page_size=60" : null);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateContact(v, t) || (!date || !time ? t("form.required") : null);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      await api.post("/appointments", { ...trackingPayload(lang), source: "viewing_form", ...v, email: v.email || null, property_id: prop || null,
        project_id: projectId || null, preferred_date: date, preferred_time: time, mode, notes: notes || null });
      toast.success(t("form.viewingSuccess"));
      setDone(true);
      setV({ name: "", phone: "", email: "", consent: false, website: "" });
      setNotes("");
      onDone?.();
    } catch (ex) {
      toast.error(`${t("form.error")}: ${errMsg(ex)}`);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} noValidate data-testid={`${prefix}-form`} className="relative space-y-4">
      {choosable && (
        <div>
          <FieldLabel optional>{t("form.property")}</FieldLabel>
          <Sel testid={`${prefix}-property-select`} value={prop} onChange={setProp} placeholder={t("form.anyProperty")}
            options={(list.data?.items || []).map((p) => [p.id, `${tr(p, "title")} · ${p.ref}`])} />
        </div>
      )}
      <ContactFields v={v} set={set} setV={setV} prefix={prefix} />
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor={`${prefix}-date`}>{t("form.date")} *</FieldLabel>
          <input id={`${prefix}-date`} data-testid={`${prefix}-date-input`} type="date" min={today()} required className="field [color-scheme:dark]" value={date} onChange={(e) => setDate(e.target.value)} />
          {date && <p data-testid={`${prefix}-date-hijri`} className="mt-1.5 text-xs text-cta/80">{t("form.hijri")}: {fmtHijri(date, lang)}</p>}
        </div>
        <div>
          <FieldLabel>{t("form.time")} *</FieldLabel>
          <Sel testid={`${prefix}-time-select`} value={time} onChange={setTime} placeholder="--:--" options={SLOTS.map((s) => [s, s])} />
        </div>
      </div>
      <fieldset>
        <legend className="mb-2 text-xs font-medium uppercase tracking-wider text-fg/60">{t("form.mode")}</legend>
        <div className="flex gap-2">
          {["in_person", "virtual"].map((m) => (
            <button key={m} type="button" data-testid={`${prefix}-mode-${m}`} aria-pressed={mode === m} onClick={() => setMode(m)} className={`chip ${mode === m ? "chip-on" : ""}`}>{t(`form.${m}`)}</button>
          ))}
        </div>
      </fieldset>
      <div>
        <FieldLabel htmlFor={`${prefix}-notes`} optional>{t("form.notes")}</FieldLabel>
        <textarea id={`${prefix}-notes`} data-testid={`${prefix}-notes-input`} className="field min-h-[80px]" maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <ConsentBox v={v} setV={setV} prefix={prefix} />
      <button type="submit" disabled={busy} data-testid={`${prefix}-submit-button`} className="btn-cta w-full">{busy ? t("form.sending") : t("form.bookViewing")}</button>
      {done && <p role="status" data-testid={`${prefix}-success-message`} className="text-center text-sm text-cta">{t("form.viewingSuccess")}</p>}
    </form>
  );
}
